import { useState } from "react";
import { confirm } from "@/lib/dialog";
import { findCrewConflicts } from "@/lib/conflicts";
import { makeDoubleBookKey } from "@/lib/scheduleUtils";

// Wraps patchBlock / addAssignment with crew double-booking pre-checks.
//
// When a block move or crew assignment would put the same person on two
// overlapping blocks, we prompt the user. If they accept, the conflict is
// remembered in `doubleBookKeys` so the grid renders the amber stripe / ring
// on every affected row. Removing the assignment clears the override.
//
// Returns the wrapped handlers plus the override Set + setter (so callers
// can clear it on a full refresh if they want).
export function useDoubleBookHandlers({
  data,
  patchBlock,
  addAssignment,
  addTaskAssignment,
  removeAssignment,
}) {
  const [doubleBookKeys, setDoubleBookKeys] = useState(() => new Set());

  const handlePatchBlock = async (blockId, updates) => {
    // Only relevant when start/end are changing — pure room or lock changes
    // can't cause new crew overlaps.
    if (!updates.start_at && !updates.end_at) {
      return patchBlock(blockId, updates);
    }
    const block = data.schedule_blocks.find((b) => b.id === blockId);
    if (!block) return patchBlock(blockId, updates);
    // Build a hypothetical "after" block to check overlaps against.
    const afterBlock = {
      ...block,
      start_at: updates.start_at || block.start_at,
      end_at: updates.end_at || block.end_at,
    };
    const crewIds = data.assignments
      .filter((a) => a.schedule_block_id === blockId)
      .map((a) => a.crew_member_id);
    const conflicts = [];
    for (const crewId of crewIds) {
      const c = findCrewConflicts(data.schedule_blocks, data.assignments, afterBlock, crewId);
      for (const conf of c) conflicts.push({ crewId, ...conf });
    }
    if (conflicts.length > 0) {
      const lines = conflicts.slice(0, 3).map((c) => {
        const crew = data.crew_members.find((x) => x.id === c.crewId);
        const task = data.tasks.find((t) => t.id === c.block.task_id);
        const start = c.block.start_at.slice(11, 16);
        const end = c.block.end_at.slice(11, 16);
        return `• ${crew?.name || "crew"} → "${task?.title || "another task"}" ${start}–${end}`;
      });
      const more = conflicts.length > 3 ? `\n…and ${conflicts.length - 3} more` : "";
      const ok = await confirm({
        title: `Double-booking ${conflicts.length} crew member${conflicts.length === 1 ? "" : "s"}`,
        body: `${lines.join("\n")}${more}\n\nMove the task anyway?`,
        confirmLabel: "MOVE ANYWAY",
        danger: true,
      });
      if (!ok) return;
      setDoubleBookKeys((prev) => {
        const next = new Set(prev);
        for (const c of conflicts) {
          next.add(makeDoubleBookKey(c.crewId, blockId, c.block.id));
        }
        return next;
      });
    }
    return patchBlock(blockId, updates);
  };

  const handleAssignCrew = async (blockId, crewMemberId, opts = {}) => {
    const targetBlock = data.schedule_blocks.find((b) => b.id === blockId);
    if (!targetBlock) return null;
    const conflicts = findCrewConflicts(
      data.schedule_blocks,
      data.assignments,
      targetBlock,
      crewMemberId,
    );
    if (conflicts.length > 0) {
      const c = conflicts[0];
      const t = data.tasks.find((tt) => tt.id === c.block.task_id);
      const crew = data.crew_members.find((cc) => cc.id === crewMemberId);
      const startHM = c.block.start_at.slice(11, 16);
      const endHM = c.block.end_at.slice(11, 16);
      const ok = await confirm({
        title: `${crew?.name || "This person"} is double-booked`,
        body: `Already on "${t?.title || "another task"}" from ${startHM}–${endHM}.\n\nAssign anyway?`,
        confirmLabel: "ASSIGN ANYWAY",
        danger: true,
      });
      if (!ok) return null;
      setDoubleBookKeys((prev) => {
        const next = new Set(prev);
        for (const conf of conflicts) {
          next.add(makeDoubleBookKey(crewMemberId, blockId, conf.block.id));
        }
        return next;
      });
    }
    return addAssignment(blockId, crewMemberId, opts);
  };

  // Task-scoped crew assign — used in ROOMS view on unscheduled (floating)
  // tasks. No time-window collisions to check (the task has no start/end yet)
  // so we just stage it. The backend migrates it onto the new schedule_block
  // when the task is later scheduled.
  const handleAssignCrewToTask = async (taskId, crewMemberId, opts = {}) => {
    return addTaskAssignment(taskId, crewMemberId, opts);
  };

  // Unassign always — clears any double-book overrides involving this pairing.
  const handleUnassignCrew = async (assignmentId) => {
    const a = data.assignments.find((x) => x.id === assignmentId);
    const ok = await removeAssignment(assignmentId);
    if (ok && a) {
      setDoubleBookKeys((prev) => {
        const next = new Set();
        for (const k of prev) {
          if (!k.startsWith(`${a.crew_member_id}|`)) { next.add(k); continue; }
          if (!k.includes(`|${a.schedule_block_id}|`) && !k.endsWith(`|${a.schedule_block_id}`)) {
            next.add(k);
          }
        }
        return next;
      });
    }
    return ok;
  };

  return {
    doubleBookKeys,
    setDoubleBookKeys,
    handlePatchBlock,
    handleAssignCrew,
    handleAssignCrewToTask,
    handleUnassignCrew,
  };
}
